// ════════════════════════════════════════════════════════
//  features/veiculos/hooks/useVeiculos.ts
// ════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { veiculoService } from '../services/veiculoService';
import type { SalvarVeiculoRequest } from '../types';

const QUERY_KEY = 'veiculos';

export function useVeiculos(busca?: string, pagina = 1, tamanhoPagina = 50) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: [QUERY_KEY, busca, pagina, tamanhoPagina],
    queryFn: () => veiculoService.listar(busca, pagina, tamanhoPagina),
    // Mantém a lista anterior enquanto a nova busca carrega: sem isso cada tecla
    // vira query nova com isLoading=true e as telas trocam tudo (input incluso) pelo
    // "Carregando...", derrubando o foco (chamados 428/429, mesma causa do 417).
    placeholderData: keepPreviousData,
  });

  const obterPorId = (id: number) =>
    veiculoService.obterPorId(id);

  const criar = useMutation({
    mutationFn: (req: SalvarVeiculoRequest) => veiculoService.criar(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, req }: { id: number; req: SalvarVeiculoRequest }) =>
      veiculoService.atualizar(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });

  const excluir = useMutation({
    mutationFn: (id: number) => veiculoService.excluir(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY] }),
  });

  return { query, obterPorId, criar, atualizar, excluir };
}

export function useVeiculosCombo() {
  return useQuery({
    queryKey: [QUERY_KEY, 'combo'],
    queryFn: veiculoService.combo,
  });
}
